/**
 * StaffShift — one row in the Schedule day-view's Staff Coverage strip.
 * Shows a staffer's name, role-for-the-day, and a horizontal bar
 * positioned over the day's time-grid for their shift window.
 *
 * Coordinated with CalendarGrid: both use the same `dayStartHour` /
 * `dayEndHour` constants so the bar lines up with the grid columns.
 * Pure presentational — no API calls, no state.
 */
import { useMemo } from 'react';
import { JobFunctionChip } from './JobFunctionChip.js';

export interface StaffShiftRowProps {
  name: string;
  /**
   * #427 W2 — the JOB FUNCTION for these hours (`kennel_tech`, `receptionist`,
   * …), never the RBAC login role. Rendered as a labelled chip, never as the
   * raw enum string.
   */
  role: string;
  /** HH:MM, e.g. "08:00" */
  shiftStart: string;
  /** HH:MM, e.g. "16:00" */
  shiftEnd: string;
  /** First hour shown by the grid (24h). */
  dayStartHour: number;
  /** Hour AFTER the last shown hour (24h). For 7am–7pm grid, pass 19. */
  dayEndHour: number;
}

function hhmmToMinutes(hhmm: string): number {
  const parts = hhmm.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? '0');
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function StaffShiftRow({
  name,
  role,
  shiftStart,
  shiftEnd,
  dayStartHour,
  dayEndHour,
}: StaffShiftRowProps): JSX.Element {
  const { leftPct, widthPct, label } = useMemo(() => {
    const dayStart = dayStartHour * 60;
    const dayEnd = dayEndHour * 60;
    const range = Math.max(1, dayEnd - dayStart);
    const start = clamp(hhmmToMinutes(shiftStart), dayStart, dayEnd);
    const end = clamp(hhmmToMinutes(shiftEnd), dayStart, dayEnd);
    const safeEnd = Math.max(start + 15, end); // min 15-min slot
    const left = ((start - dayStart) / range) * 100;
    const width = ((safeEnd - start) / range) * 100;
    return {
      leftPct: left,
      widthPct: width,
      label: `${shiftStart}–${shiftEnd}`,
    };
  }, [shiftStart, shiftEnd, dayStartHour, dayEndHour]);

  return (
    <div className="ferro-staff-shift-row" data-testid="staff-shift-row">
      <div className="ferro-staff-shift-row__label">
        <span className="ferro-staff-shift-row__name">{name}</span>
        <JobFunctionChip role={role} className="ferro-staff-shift-row__role" />
      </div>
      <div className="ferro-staff-shift-row__track" aria-hidden="false">
        <div
          className="ferro-staff-shift-row__bar"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
          title={`${name} · ${label}`}
        >
          <span className="ferro-staff-shift-row__bar-text">{label}</span>
        </div>
      </div>
    </div>
  );
}
